// Campos de fondo del bloque para el editor de Puck.
//
// Forman parte del "contrato de superficie" del sitio: el usuario elige un ROL
// de fondo (swatches) o un color PERSONALIZADO + tono. El renderer consume eso
// vía blockShell() → data-bg / data-tone / --block-bg, y el texto se adapta solo.
//
// Limitación de Puck: un campo custom solo escribe SU propia clave. Por eso el
// fondo se reparte en tres sub-campos hermanos del grupo "Diseño":
//   bg (rol o 'custom') · bgColor (color libre) · tone (claro/oscuro).

interface RenderProps {
  value?: string;
  onChange: (v: string) => void;
}

// Colores representativos para los swatches de rol. No son los exactos del tema
// del tenant (esos viven en el iframe de "Vista en vivo"); dan una pista visual.
const BG_SWATCHES: Array<{ value: string; label: string; color?: string; custom?: boolean; ring?: boolean }> = [
  { value: 'default',  label: 'Por defecto',        color: '#ffffff', ring: true },
  { value: 'surface',  label: 'Tarjeta',            color: '#f4f1ea' },
  { value: 'alt',      label: 'Alternativo',        color: '#e9e2d4' },
  { value: 'deep',     label: 'Oscuro',             color: '#1e1c19' },
  { value: 'deep-alt', label: 'Oscuro alternativo', color: '#2d2a25' },
  { value: 'custom',   label: 'Personalizado',      custom: true },
];

export function BgSwatchField({ value, onChange }: RenderProps) {
  const current = value ?? 'default';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {BG_SWATCHES.map((s) => {
        const active = current === s.value;
        return (
          <button
            type="button"
            key={s.value}
            title={s.label}
            aria-label={s.label}
            aria-pressed={active}
            onClick={() => onChange(s.value)}
            style={{
              width: 38,
              height: 38,
              borderRadius: 9,
              cursor: 'pointer',
              padding: 0,
              outline: 'none',
              boxShadow: active ? '0 0 0 2px rgb(var(--ui-accent-ch))' : 'none',
              border: s.ring ? '1px solid rgb(var(--ui-border-ch))' : '1px solid rgba(0,0,0,0.12)',
              background: s.custom
                ? 'conic-gradient(from 210deg, #f87171, #fbbf24, #34d399, #60a5fa, #a78bfa, #f87171)'
                : s.color,
            }}
          />
        );
      })}
    </div>
  );
}

const HEX = /^#[0-9a-fA-F]{3,8}$/;

// Selector de color genérico: picker + hex + botón para quitar el color. Se usa
// para el color de fondo personalizado y para los overrides de texto/botones/
// tarjetas, cuyo estado por defecto es "sin color" (hereda el tema). Por eso el
// botón de limpiar es importante: deja el valor vacío → sin override.
export function ColorField({ value, onChange }: RenderProps) {
  const v = value ?? '';
  const hexForPicker = HEX.test(v) ? v : '#3b82f6';
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="color"
        value={hexForPicker}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 42,
          height: 34,
          padding: 0,
          border: '1px solid rgb(var(--ui-border-ch))',
          borderRadius: 8,
          background: 'transparent',
          cursor: 'pointer',
        }}
      />
      <input
        type="text"
        value={v}
        placeholder="#RRGGBB"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          height: 34,
          padding: '0 10px',
          fontSize: '0.875rem',
          color: 'rgb(var(--ui-text-ch))',
          background: 'rgb(var(--ui-surface-ch))',
          border: '1px solid rgb(var(--ui-border-ch))',
          borderRadius: 8,
          outline: 'none',
        }}
      />
      {v && (
        <button
          type="button"
          title="Quitar color"
          aria-label="Quitar color"
          onClick={() => onChange('')}
          style={{
            height: 34,
            padding: '0 10px',
            fontSize: '0.875rem',
            color: 'rgb(var(--ui-text-muted-ch))',
            background: 'transparent',
            border: '1px solid rgb(var(--ui-border-ch))',
            borderRadius: 8,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Quitar
        </button>
      )}
    </div>
  );
}

/** Alias retrocompatible para el campo de color de fondo. */
export const BgColorField = ColorField;
